import { useEffect, useRef, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { isProduction } from '../../../../../config/config'
import { type AgentEvalsStreamEvent, streamEvals } from '../../../../../models/agentAdmin'

enum EvalsStatus {
  IDLE = 'idle',
  RUNNING = 'running',
  PASSED = 'passed',
  FAILED = 'failed',
  ERROR = 'error',
}

export function EvalsTab() {
  const [status, setStatus] = useState<EvalsStatus>(EvalsStatus.IDLE)
  const [output, setOutput] = useState<string[]>([])
  const outputRef = useRef<HTMLPreElement>(null)

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight
    }
  }, [output])

  if (isProduction) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Agent Evals</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            For security reasons, evals can only be run in staging. Switch to staging to run the eval suite.
          </p>
        </CardContent>
      </Card>
    )
  }

  async function handleRunEvals() {
    setStatus(EvalsStatus.RUNNING)
    setOutput([])

    try {
      await streamEvals((event: AgentEvalsStreamEvent) => {
        switch (event.type) {
          case 'output':
            setOutput((prev) => [...prev, event.line])
            break
          case 'finished':
            setStatus(event.success ? EvalsStatus.PASSED : EvalsStatus.FAILED)
            break
          case 'error':
            setOutput((prev) => [...prev, `Error: ${event.error}\n`])
            setStatus(EvalsStatus.ERROR)
            break
        }
      })
    } catch {
      setStatus(EvalsStatus.ERROR)
      setOutput((prev) => [...prev, 'Failed to connect to evals endpoint.\n'])
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Agent Evals
            {status !== EvalsStatus.IDLE && status !== EvalsStatus.RUNNING && (
              <Badge variant={status === EvalsStatus.PASSED ? 'success' : 'destructive'}>
                {status === EvalsStatus.PASSED
                  ? 'Passed'
                  : status === EvalsStatus.FAILED
                    ? 'Failed'
                    : 'Error'}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleRunEvals}
              loading={status === EvalsStatus.RUNNING ? 'Running…' : undefined}
            >
              Run Evals
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {output.length > 0 ? (
          <pre
            ref={outputRef}
            className="max-h-[600px] overflow-auto rounded-md bg-gray-900 p-4 font-mono text-xs text-gray-100"
          >
            {output.join('')}
          </pre>
        ) : (
          <p className="text-sm text-gray-500">
            Click &quot;Run Evals&quot; to execute eval tests (pytest -m eval).
          </p>
        )}
      </CardContent>
    </Card>
  )
}
